import React, { useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, Plus, Trash2, Save, X, Pencil } from "lucide-react";
import { API } from "@/lib/faro";
import { toast } from "sonner";

const DEFAULT_LABELS = {
  A: "Curso parcial (1 día + 3 h online · Tipo A)",
  B1: "Curso total 20 h (Tipos B1 / B2 / B3)",
};
const DEFAULT_PRICES = { puntos: 215, carnet: 405 };
const CODES = [
  { code: "A", defaultLabel: DEFAULT_LABELS.A },
  { code: "B1", defaultLabel: DEFAULT_LABELS.B1 },
];
const PRICE_ROWS = [
  { key: "puntos", label: "Curso de puntos (Tipo A · parcial)", defaultValue: DEFAULT_PRICES.puntos },
  { key: "carnet", label: "Curso de carnet (Tipos B1 / B2 / B3 · total)", defaultValue: DEFAULT_PRICES.carnet },
];

function isPast(startDate) {
  const y = new Date();
  const today = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
  return startDate < today;
}

export default function CourseSchedulePanel({ authHeader }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "A", start_date: "", end_date: "", seats_total: "", seats_remaining: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [labels, setLabels] = useState(DEFAULT_LABELS);
  const [editingLabels, setEditingLabels] = useState(false);
  const [labelDraft, setLabelDraft] = useState(DEFAULT_LABELS);
  const [savingLabels, setSavingLabels] = useState(false);
  const [prices, setPrices] = useState(DEFAULT_PRICES);
  const [editingPrices, setEditingPrices] = useState(false);
  const [priceDraft, setPriceDraft] = useState(DEFAULT_PRICES);
  const [savingPrices, setSavingPrices] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: sch }, { data: cfg }] = await Promise.all([
        axios.get(`${API}/admin/course-schedule`, { headers: authHeader }),
        axios.get(`${API}/admin/config`, { headers: authHeader }),
      ]);
      setRows(sch || []);
      const storedL = cfg?.course_type_labels || {};
      const mergedL = { ...DEFAULT_LABELS, ...storedL };
      setLabels(mergedL);
      setLabelDraft(mergedL);
      const storedP = cfg?.course_type_prices || {};
      const mergedP = { ...DEFAULT_PRICES, ...storedP };
      setPrices(mergedP);
      setPriceDraft(mergedP);
    } catch (e) {
      toast.error("No se pudo cargar el calendario");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveLabels = async () => {
    // Validación: ambos deben tener texto tras trim
    const cleaned = {
      A: (labelDraft.A || "").trim() || DEFAULT_LABELS.A,
      B1: (labelDraft.B1 || "").trim() || DEFAULT_LABELS.B1,
    };
    setSavingLabels(true);
    try {
      await axios.put(`${API}/admin/config`, { course_type_labels: cleaned }, { headers: authHeader });
      setLabels(cleaned);
      setLabelDraft(cleaned);
      setEditingLabels(false);
      toast.success("Duraciones actualizadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar las duraciones");
    } finally {
      setSavingLabels(false);
    }
  };

  const resetLabels = () => {
    setLabelDraft(DEFAULT_LABELS);
  };

  const savePrices = async () => {
    const cleaned = {};
    for (const { key, defaultValue } of PRICE_ROWS) {
      const raw = priceDraft[key];
      const n = typeof raw === "number" ? raw : parseInt(String(raw ?? "").trim(), 10);
      if (Number.isFinite(n) && n > 0) cleaned[key] = n;
      else cleaned[key] = defaultValue;
    }
    setSavingPrices(true);
    try {
      await axios.put(`${API}/admin/config`, { course_type_prices: cleaned }, { headers: authHeader });
      setPrices(cleaned);
      setPriceDraft(cleaned);
      setEditingPrices(false);
      toast.success("Precios actualizados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar los precios");
    } finally {
      setSavingPrices(false);
    }
  };

  const resetPrices = () => setPriceDraft(DEFAULT_PRICES);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Introduce fechas de inicio y fin");
    if (form.end_date < form.start_date) return toast.error("La fecha de fin no puede ser anterior al inicio");
    try {
      const payload = {
        code: form.code,
        start_date: form.start_date,
        end_date: form.end_date,
        seats_total: form.seats_total ? Number(form.seats_total) : null,
        seats_remaining: form.seats_remaining ? Number(form.seats_remaining) : null,
      };
      await axios.post(`${API}/admin/course-schedule`, payload, { headers: authHeader });
      toast.success("Convocatoria añadida");
      setForm({ code: form.code, start_date: "", end_date: "", seats_total: "", seats_remaining: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      start_date: r.start_date,
      end_date: r.end_date,
      seats_total: r.seats_total ?? "",
      seats_remaining: r.seats_remaining ?? "",
    });
  };

  const saveEdit = async (id) => {
    try {
      const payload = {
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        seats_total: editForm.seats_total === "" ? null : Number(editForm.seats_total),
        seats_remaining: editForm.seats_remaining === "" ? null : Number(editForm.seats_remaining),
      };
      await axios.put(`${API}/admin/course-schedule/${id}`, payload, { headers: authHeader });
      toast.success("Convocatoria actualizada");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al actualizar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta convocatoria? Esta acción es irreversible.")) return;
    try {
      await axios.delete(`${API}/admin/course-schedule/${id}`, { headers: authHeader });
      toast.success("Eliminada");
      await load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const byCode = (c) => rows.filter((r) => r.code === c);

  return (
    <div className="space-y-8" data-testid="course-schedule-panel">
      <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <CalendarClock size={18} className="text-[var(--faro-blue)]" />
          <h2 className="font-display font-black text-xl">Cursos de puntos · Calendario</h2>
        </div>
        <p className="text-sm text-[var(--faro-text-soft)]">
          Alta y baja de convocatorias. Las convocatorias cuyo inicio fue <strong>ayer o antes</strong> se ocultan
          automáticamente de la web pública (siguen guardadas aquí). Los cambios en el <strong>JSON-LD del schema</strong>
          se aplican en el próximo redespliegue.
        </p>
      </div>

      <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-5" data-testid="course-labels-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-black text-lg">Duración de cada curso</h3>
            <p className="text-xs text-[var(--faro-text-soft)] mt-1">
              Este texto aparece como cabecera del tipo de curso aquí y en el <strong>banner &ldquo;Próximo curso&rdquo;</strong> de la portada.
              Edítalo si cambian las horas presenciales u online.
            </p>
          </div>
          {!editingLabels ? (
            <button
              type="button"
              onClick={() => { setLabelDraft(labels); setEditingLabels(true); }}
              className="btn-pill btn-ghost text-xs"
              data-testid="course-labels-edit"
            >
              <Pencil size={14} /> Editar duraciones
            </button>
          ) : (
            <div className="inline-flex gap-1">
              <button type="button" onClick={saveLabels} disabled={savingLabels} className="btn-pill btn-primary text-xs" data-testid="course-labels-save">
                <Save size={14} /> {savingLabels ? "Guardando…" : "Guardar"}
              </button>
              <button type="button" onClick={() => { setLabelDraft(labels); setEditingLabels(false); }} className="btn-pill btn-ghost text-xs">
                <X size={14} />
              </button>
              <button type="button" onClick={resetLabels} className="btn-pill btn-ghost text-xs" data-testid="course-labels-reset" title="Volver a los valores por defecto">
                Restablecer
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {CODES.map(({ code, defaultLabel }) => (
            <div key={code}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[var(--faro-blue)] mb-1">Tipo {code}</label>
              {editingLabels ? (
                <input
                  type="text"
                  value={labelDraft[code] || ""}
                  onChange={(e) => setLabelDraft((d) => ({ ...d, [code]: e.target.value }))}
                  data-testid={`course-labels-input-${code}`}
                  placeholder={defaultLabel}
                  className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm focus:border-[var(--faro-blue)] outline-none"
                />
              ) : (
                <p className="text-sm font-semibold" data-testid={`course-labels-view-${code}`}>{labels[code] || defaultLabel}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-5" data-testid="course-prices-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-black text-lg">Precio de cada curso</h3>
            <p className="text-xs text-[var(--faro-text-soft)] mt-1">
              Precio público (en euros, sin decimales). Se aplica al <strong>hero</strong>, <strong>FAQ</strong>, <strong>chip de precio</strong> y <strong>schema de Google</strong> de la página de recuperación de puntos, y al banner &ldquo;Próximo curso&rdquo;. Un cambio se refleja al instante, sin redeploy.
            </p>
          </div>
          {!editingPrices ? (
            <button
              type="button"
              onClick={() => { setPriceDraft(prices); setEditingPrices(true); }}
              className="btn-pill btn-ghost text-xs"
              data-testid="course-prices-edit"
            >
              <Pencil size={14} /> Editar precios
            </button>
          ) : (
            <div className="inline-flex gap-1">
              <button type="button" onClick={savePrices} disabled={savingPrices} className="btn-pill btn-primary text-xs" data-testid="course-prices-save">
                <Save size={14} /> {savingPrices ? "Guardando…" : "Guardar"}
              </button>
              <button type="button" onClick={() => { setPriceDraft(prices); setEditingPrices(false); }} className="btn-pill btn-ghost text-xs">
                <X size={14} />
              </button>
              <button type="button" onClick={resetPrices} className="btn-pill btn-ghost text-xs" data-testid="course-prices-reset" title="Volver a los valores por defecto (215 / 405)">
                Restablecer
              </button>
            </div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {PRICE_ROWS.map(({ key, label, defaultValue }) => (
            <div key={key}>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-[var(--faro-blue)] mb-1">{label}</label>
              {editingPrices ? (
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={priceDraft[key] ?? ""}
                    onChange={(e) => setPriceDraft((d) => ({ ...d, [key]: e.target.value }))}
                    data-testid={`course-prices-input-${key}`}
                    placeholder={String(defaultValue)}
                    className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm focus:border-[var(--faro-blue)] outline-none pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-[var(--faro-text-soft)]">€</span>
                </div>
              ) : (
                <p className="text-sm font-black text-[var(--faro-red)]" data-testid={`course-prices-view-${key}`}>{prices[key] ?? defaultValue} €</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submitCreate} className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-5 grid grid-cols-1 md:grid-cols-6 gap-3 items-end" data-testid="course-schedule-form">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1">Tipo</label>
          <select className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="course-schedule-code">
            {CODES.map((c) => <option key={c.code} value={c.code}>{labels[c.code] || c.defaultLabel}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1">Inicio</label>
          <input type="date" className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="course-schedule-start" required />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1">Fin</label>
          <input type="date" className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="course-schedule-end" required />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1">Plazas total</label>
          <input type="number" min="0" className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm" value={form.seats_total} onChange={(e) => setForm({ ...form, seats_total: e.target.value })} data-testid="course-schedule-seats-total" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1">Plazas restantes</label>
          <input type="number" min="0" className="w-full rounded-lg border border-[rgba(30,42,143,0.2)] p-2 text-sm" value={form.seats_remaining} onChange={(e) => setForm({ ...form, seats_remaining: e.target.value })} data-testid="course-schedule-seats-remaining" />
        </div>
        <button type="submit" className="btn-pill btn-primary justify-center text-sm h-10" data-testid="course-schedule-add">
          <Plus size={16} /> Añadir
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-[var(--faro-text-soft)]">Cargando…</p>
      ) : (
        CODES.map(({ code, defaultLabel }) => (
          <div key={code} className="rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white overflow-hidden" data-testid={`course-schedule-table-${code}`}>
            <div className="bg-[rgba(30,42,143,0.04)] p-4 border-b border-[rgba(30,42,143,0.1)]">
              <h3 className="font-display font-black text-lg" data-testid={`course-schedule-header-${code}`}>{labels[code] || defaultLabel}</h3>
              <p className="text-xs text-[var(--faro-text-soft)]">{byCode(code).length} convocatorias guardadas</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left bg-[rgba(30,42,143,0.02)] text-xs uppercase font-bold text-[var(--faro-text-soft)]">
                  <th className="p-3">Inicio</th>
                  <th className="p-3">Fin</th>
                  <th className="p-3">Plazas total</th>
                  <th className="p-3">Plazas restantes</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {byCode(code).length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-[var(--faro-text-soft)]">Sin convocatorias.</td></tr>
                )}
                {byCode(code).map((r) => {
                  const past = isPast(r.start_date);
                  const editing = editingId === r.id;
                  return (
                    <tr key={r.id} className={`border-t border-[rgba(30,42,143,0.06)] ${past ? "opacity-60" : ""}`} data-testid={`course-schedule-row-${r.id}`}>
                      <td className="p-3">
                        {editing ? <input type="date" className="rounded border p-1 text-sm" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} /> : <strong>{r.start_date}</strong>}
                      </td>
                      <td className="p-3">
                        {editing ? <input type="date" className="rounded border p-1 text-sm" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} /> : r.end_date}
                      </td>
                      <td className="p-3">
                        {editing ? <input type="number" min="0" className="rounded border p-1 text-sm w-20" value={editForm.seats_total} onChange={(e) => setEditForm({ ...editForm, seats_total: e.target.value })} /> : (r.seats_total ?? "—")}
                      </td>
                      <td className="p-3">
                        {editing ? <input type="number" min="0" className="rounded border p-1 text-sm w-20" value={editForm.seats_remaining} onChange={(e) => setEditForm({ ...editForm, seats_remaining: e.target.value })} /> : (r.seats_remaining ?? "—")}
                      </td>
                      <td className="p-3">
                        {past ? <span className="text-xs uppercase font-bold text-[var(--faro-text-soft)]">Pasada · oculta</span> : <span className="text-xs uppercase font-bold text-[var(--faro-blue)]">Activa</span>}
                      </td>
                      <td className="p-3 text-right">
                        {editing ? (
                          <div className="inline-flex gap-1">
                            <button onClick={() => saveEdit(r.id)} className="btn-pill btn-primary text-xs" data-testid={`course-schedule-save-${r.id}`}><Save size={14} /> Guardar</button>
                            <button onClick={() => setEditingId(null)} className="btn-pill btn-ghost text-xs"><X size={14} /></button>
                          </div>
                        ) : (
                          <div className="inline-flex gap-1">
                            <button onClick={() => startEdit(r)} className="btn-pill btn-ghost text-xs" data-testid={`course-schedule-edit-${r.id}`}>Editar</button>
                            <button onClick={() => remove(r.id)} className="btn-pill btn-ghost text-xs text-[var(--faro-red)] hover:bg-[rgba(230,57,70,0.08)]" data-testid={`course-schedule-delete-${r.id}`}><Trash2 size={14} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
